import { BaseApiClient } from './base-client';

export interface ActivityEntry {
  timestamp: string;
  username: string;
  action: string;
  details: string;
  source_ip: string | null;
}

export interface ActivityResponse {
  activities: ActivityEntry[];
  summary: {
    total_events: number;
    successful_logins: number;
    failed_logins: number;
    unique_users: number;
    hours_covered: number;
  };
}

export interface QuotaInfo {
  current: {
    used_mb: number;
    quota_mb: number;
    files_count: number;
    last_updated: string;
    usage_percent: number;
    ldap_quota_mb?: number;
  };
  history: Array<{
    timestamp: string;
    used_mb: number;
    quota_mb: number;
  }>;
}

export interface UserQuotaSummary {
  username: string;
  used_mb: number;
  quota_mb: number;
  usage_percent: number;
  files_count: number;
}

export interface QuotaSummaryResponse {
  users: UserQuotaSummary[];
  summary: {
    total_users: number;
    total_used_mb: number;
    total_quota_mb: number;
    total_files: number;
    average_usage_percent: number;
  };
}

export interface TaskExecution {
  id: number;
  job_id: string;
  start_time: string;
  end_time: string;
  duration: number;
  status: string;
  error: string | null;
}

export interface TaskHistoryResponse {
  executions: TaskExecution[];
  summary: {
    total_executions: number;
    failed_executions: number;
    success_rate: number;
    average_duration: number;
  };
}

export interface MonitoringAlert {
  id: number;
  level: string;
  message: string;
  created_at: string;
}

export interface AlertsResponse {
  alerts: MonitoringAlert[];
  summary: {
    total_alerts: number;
    alerts_by_level: Record<string, number>;
  };
}

export class MonitoringAPIClient extends BaseApiClient {
  // Service activity endpoints
  async getServiceActivity(serviceId: number, hours: number = 24): Promise<ActivityResponse> {
    return this.get<ActivityResponse>(`/api/v1/monitoring/services/${serviceId}/activity`, {
      params: { hours }
    });
  }

  async getUserActivity(serviceId: number, username: string, hours: number = 24): Promise<ActivityResponse> {
    return this.get<ActivityResponse>(`/api/v1/monitoring/services/${serviceId}/users/${username}/activity`, {
      params: { hours }
    });
  }

  // Quota endpoints
  async getServiceQuotaSummary(serviceId: number): Promise<QuotaSummaryResponse> {
    return this.get<QuotaSummaryResponse>(`/api/v1/monitoring/services/${serviceId}/quota`);
  }

  async getUserQuota(serviceId: number, username: string): Promise<QuotaInfo> {
    return this.get<QuotaInfo>(`/api/v1/monitoring/services/${serviceId}/users/${username}/quota`);
  }

  // System monitoring endpoints
  async getTaskHistory(hours: number = 24, jobId?: string): Promise<TaskHistoryResponse> {
    const params: any = { hours };
    if (jobId) params.job_id = jobId;
    
    return this.get<TaskHistoryResponse>('/api/v1/monitoring/tasks', { params });
  }

  async getMonitoringAlerts(hours: number = 24, level?: string): Promise<AlertsResponse> {
    const params: any = { hours };
    if (level) params.level = level;
    
    return this.get<AlertsResponse>('/api/v1/monitoring/alerts', { params });
  }

  // Service status endpoint
  async getServiceStatus(serviceId: number): Promise<any> {
    return this.get<any>(`/api/v1/monitoring/services/${serviceId}/status`);
  }
}